import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '../../../contexts/ThemeContext';
import { Link, useNavigate } from 'react-router-dom';
import authService from '../../../services/candidate/authService';
import { useSavedJobs } from '../../../hooks/useSavedJobs';
import { 
    toTitleCase, 
    cleanLocationString, 
    formatSalary, 
    formatExperience,
    getJobIcon, 
    getJobIconBgColor, 
    getJobIconColor    
} from '../../../utils/jobDataTransform';
import { formatRelativeTime } from '../../../utils/dateFormatter';
import ApplyEmailModal from '../../../components/jobs/ApplyEmailModal';

const SavedJobsContent = () => {
    const navigate = useNavigate();
    
    // Auth state
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    
    // Apply email modal state
    const [showApplyEmailModal, setShowApplyEmailModal] = useState(false);
    const [selectedJobForEmail, setSelectedJobForEmail] = useState(null);
    
    useEffect(() => {
        const isAuth = authService.isAuthenticated();
        setIsLoggedIn(!!isAuth);
        
        // Redirect to login if not logged in
        if (!isAuth) {
            navigate('/candidate/login');
        }
    }, [navigate]);

    // Use the saved jobs hook
    const { 
        savedJobs, 
        savedJobsIds, 
        loading, 
        toggleSaveJob 
    } = useSavedJobs(isLoggedIn);

    // Handle external job click (for non-hunarpool jobs)
    const handleExternalJobClick = (job) => {
        const jobData = {
            id: job.id,
            title: job.title,
            company: job.company,
            category: job.category,
            categoryLabel: job.categoryLabel,
            location: job.location,
            postedTime: job.postedTime,
            type: job.type,
            salary: job.salary,
            experience: job.experience,
            description: job.description,
            skills: job.skills,
            icon: job.icon,
            iconBgColor: job.iconBgColor,
            iconColor: job.iconColor,
            redirectUrl: job.redirectUrl,
            source: job.source,
            applyEmail: job.applyEmail,
            timestamp: Date.now()
        };
        
        localStorage.setItem(`job_${job.id}`, JSON.stringify(jobData));
        navigate(`/candidate/jobs/${job.id}`, { state: { fromJobs: true, jobSource: job.source } });
    };

    // Handle apply now
    const handleApplyNow = (job) => {
        const { redirectUrl, source, applyEmail } = job;
        
        if (source === 'hunarpool' && applyEmail) {
            setSelectedJobForEmail(job);
            setShowApplyEmailModal(true);
            return;
        }
        else if(source === 'hunarpool' && redirectUrl) {
            window.open(redirectUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        else {
            window.open(redirectUrl, '_blank', 'noopener,noreferrer');
            return;
        }
    };

    // Loading state
    if (loading) {
        return (
            <div>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading your saved jobs...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* SEO Settings */}
            <title>{`Saved Jobs – Review and Apply Later | ${process.env.REACT_APP_NAME}`}</title>
            <meta name="robots" content="noindex, nofollow" />

            {/* Header */}
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-white">
                    Your Saved Jobs
                </h1>
                <p className="text-gray-400">
                    {savedJobs.length > 0 
                        ? `You have ${savedJobs.length} saved job${savedJobs.length > 1 ? 's' : ''}`
                        : 'You haven\'t saved any jobs yet'}
                </p>
            </div>

            {/* Saved Jobs List */}
            {savedJobs.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
                    <i className="far fa-heart text-gray-300 text-5xl mb-4"></i>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">No Saved Jobs</h3>
                    <p className="text-gray-600">Start saving jobs you're interested in</p>
                    <Link 
                        to="/candidate/jobs"
                        className="inline-block mt-6 bg-gradient-to-r from-[#0096C7] to-[#4361EE] hover:from-[#00a8e0] hover:to-[#4a6bf5] text-white font-medium px-6 py-3 rounded-xl hover:shadow-lg hover:shadow-[#0096C7]/20 transition-all duration-300"
                    >
                        Browse Jobs
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {savedJobs.map((savedItem) => {
                        const job = savedItem.jobData; // Extract job data from saved item
                        
                        return (
                            <div 
                                key={savedItem._id || job.id} 
                                className="bg-white rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-300 overflow-hidden"
                            >
                                <div className="p-6">
                                    {/* Top Row */}
                                    <div className="flex items-start gap-4 mb-4">
                                        <div className={`w-14 h-14 rounded-xl ${getJobIconBgColor(job.category)} flex items-center justify-center flex-shrink-0`}>
                                            <i className={`fas ${getJobIcon(job.category)} text-2xl`} style={{ color: getJobIconColor(job.category) }}></i>
                                        </div>
                                        
                                        <div className="flex-1 min-w-0">
                                            {job.source === 'hunarpool' ? (
                                                <Link 
                                                    to={`/candidate/jobs/${job.id}`}
                                                    state={{ jobData: job }}
                                                    className="text-xl font-bold text-gray-800 hover:text-primary-600 transition-colors inline-block"
                                                >
                                                    {toTitleCase(job.title)}
                                                    <p className="text-gray-500 font-medium text-base mt-1">{job.company}</p>
                                                </Link>
                                            ) : (
                                                <div 
                                                    onClick={() => handleExternalJobClick(job)}
                                                    className="cursor-pointer"
                                                >
                                                    <h3 className="text-xl font-bold text-gray-800 hover:text-primary-600 transition-colors">
                                                        {toTitleCase(job.title)}                                                            
                                                    </h3>
                                                    <p className="text-gray-500 font-medium text-base mt-1">{job.company}</p>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="flex items-center gap-3 flex-shrink-0">
                                            <button 
                                                className={`p-2 ${savedJobsIds.includes(job.id) ? 'text-primary-600' : 'text-gray-400 hover:text-primary-600'}`}
                                                onClick={() => toggleSaveJob(job)}
                                            >
                                                <i className={`${savedJobsIds.includes(job.id) ? 'fas' : 'far'} fa-heart text-xl`}></i>
                                            </button>
                                            
                                            <button 
                                                onClick={() => handleApplyNow(job)}
                                                className="hidden md:block bg-gradient-to-r from-[#0096C7] to-[#4361EE] hover:from-[#00a8e0] hover:to-[#4a6bf5] text-white font-medium px-6 py-2.5 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#0096C7]/20 text-sm"
                                            >
                                                Apply Now
                                            </button>
                                        </div>
                                    </div>

                                    {/* Location and Saved Time */}
                                    <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
                                        <span className="flex items-center text-gray-600">
                                            <i className="fas fa-map-marker-alt mr-2 text-primary-600 w-4"></i>
                                            {job.location}
                                        </span>
                                        <span className="flex items-center text-gray-600">
                                            <i className="fas fa-clock mr-2 text-primary-600 w-4"></i>
                                            Saved {formatRelativeTime(savedItem.savedAt)}
                                        </span>
                                    </div>

                                    {/* Job Details Chips */}
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        <span className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">
                                            {job.categoryLabel || 'General'}
                                        </span>
                                        <span className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">
                                            {job.type}
                                        </span>
                                        <span className="px-3 py-1.5 bg-gray-100 text-green-600 rounded-lg text-sm font-medium">
                                            {job.salary}
                                        </span>
                                        <span className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">
                                            {job.experience}
                                        </span>
                                        {job.workMode && (
                                            <span className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">
                                                {job.workMode}
                                            </span>
                                        )}
                                    </div>

                                    {/* Description */}
                                    <p className="text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                                        {job.description}
                                    </p>

                                    {/* Skills */}
                                    {job.skills && job.skills.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-6">
                                            {job.skills.slice(0, 5).map((skill, index) => (
                                                <span 
                                                    key={index} 
                                                    className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm"
                                                >
                                                    {skill}
                                                </span>
                                            ))}
                                            {job.skills.length > 5 && (
                                                <span className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm">
                                                    +{job.skills.length - 5} more
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Mobile Apply Button */}
                                    <div className="flex justify-end md:hidden">
                                        <button 
                                            onClick={() => handleApplyNow(job)}
                                            className="w-full bg-gradient-to-r from-[#0096C7] to-[#4361EE] hover:from-[#00a8e0] hover:to-[#4a6bf5] text-white font-medium px-8 py-3 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#0096C7]/20"
                                        >
                                            Apply Now
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Apply Email Modal */}
            <ApplyEmailModal 
                isOpen={showApplyEmailModal}
                onClose={() => {
                    setShowApplyEmailModal(false);
                    setSelectedJobForEmail(null);
                }}
                job={selectedJobForEmail || {}}
            />
        </div>
    );
};

const SavedJobs = () => {
    return (
        <ThemeProvider theme="candidate">
            <SavedJobsContent />
        </ThemeProvider>
    );
};

export default SavedJobs;